import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  Building,
  Tag,
  Bookmark,
  ShoppingCart,
} from "lucide-react";
import { motion } from "framer-motion";
import PartChatDialog from "@/components/PartChatDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead,
} from "../store/partChat/partChatActions";
import { openAuthDialog } from "../store/auth/authSlice";
import { toast } from "./ui/use-toast";
import {
  fetchSavedParts,
  removeSavedPart,
  savePart,
} from "../store/parts/partsAction";
import PartImageSlider from "../hooks/partImageSlider";
import PartPaymentModal from "./Modal/PartPaymentModal";
import { useNavigate } from "react-router-dom";

const PartCard = ({
  part,
  isCompact = false,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { messages, isMessagesLoading, isSenderLoading } = useSelector(
    (state) => state.partChat,
  );
  const { saveLoading, savedParts, removeLoading } = useSelector(
    (state) => state.parts,
  );
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const isSaved = savedParts?.some(
    (saved) => saved.partId === part?.id && saved.userId === user?.uid,
  );

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const handleChatClick = async () => {
    if (!isAuthenticated || !user) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login to send message to Seller.",
          variant: "destructive",
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true),
          }),
        );
      }
      return;
    }
    try {
      const sellerData = await dispatch(
        fetchSellerById({ userId: part?.sellerId }),
      ).unwrap();

      const conversation = {
        partInfo: part,
        otherUserDetail: sellerData,
      };

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: part?.sellerId,
          partId: part?.id,
          currentUserId: user?.uid,
        }),
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: part?.sellerId,
            partId: part?.id,
          }),
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("Failed to fetch seller:", error);
    }
  };

  const handleSaveClick = (part) => {
    if (!isAuthenticated || !user) return;

    if (isSaved) {
      dispatch(removeSavedPart({ userId: user?.uid, partId: part?.id }));
    } else {
      dispatch(savePart({ userId: user?.uid, part: part }));
    }
  };

  const displayPrice =
    typeof part.price === "number"
      ? part.price.toFixed(2)
      : typeof part.price === "string" && !isNaN(parseFloat(part.price))
        ? parseFloat(part.price).toFixed(2)
        : "N/A";

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800",
    },
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const tier = getCurrentTier(part);

  const getDisplayName = (item) => {
    const name =
      item?.name?.toLowerCase() === "other" ? `${item?.sub_name}` : item?.name;
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const getDisplayMakeModel = (item) => {
    if (!item) return "";

    const make = item?.make || "";
    const subMake = item?.sub_make || "";
    const model = item?.model || "";
    const subModel = item?.sub_model || "";
    const isOther =
      make?.toLowerCase() === "other" || model?.toLowerCase() === "other";
    const displayMake =
      make?.toLowerCase() === "other" && subMake ? subMake : make;
    const displayModel =
      model?.toLowerCase() === "other" && subModel ? subModel : model;
    let result = `${displayMake} ${displayModel}`.trim();
    result = result
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    if (isOther) {
      result += " (Other)";
    }

    return result;
  };

  const partName = getDisplayName(part) || "Unknown Part";
  const partYear = part?.year || "N/A";
  const partMakeModel = getDisplayMakeModel(part) || "Unknown";

  const handleBuyClick = () => {
    if (!isAuthenticated || !user) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login as buyer to buy parts.",
          variant: "destructive",
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true),
          }),
        );
      }
      return;
    }
    if (!part?.stripePaymentLink) {
      toast({
        title: "Payment Unavailable",
        description: "This seller has not added a Stripe payment link.",
        variant: "destructive",
      });
      return;
    }
    setShowPaymentModal(true);
  };

  useEffect(() => {
    if (user && isAuthenticated) {
      dispatch(fetchSavedParts({ userId: user?.uid }));
    }
  }, []);

  return (
    <motion.div variants={itemVariants} className={isCompact ? "w-full" : ""}>
      <Card
        onClick={() => navigate(`/partDetail/${part?.id}`)}
        className={`overflow-hidden hover:shadow-primary/20 cursor-pointer group hover:shadow-lg transition-shadow hover:border-primary duration-300 ease-in-out border h-full flex flex-col ${
          isCompact ? "p-2" : ""
        }`}
      >
        <CardHeader className={`p-0 ${isCompact ? "mb-2" : ""}`}>
          <div
            className={`relative ${
              isCompact ? "h-24" : "h-40"
            } bg-secondary overflow-hidden`}
          >
            <PartImageSlider
              images={
                part?.imageUrls?.length
                  ? part.imageUrls
                  : part?.imageReferences?.length
                    ? part.imageReferences
                    : part?.imageUrl
                      ? [part.imageUrl]
                      : []
              }
              enableSlider={false}
              alt={`Image of ${part.name}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            {tier && (
              <span
                className={`absolute top-1.5 right-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tier.bgColor} ${tier.textColor} ${tier.borderColor}`}
              >
                <tier.icon className="mr-1 h-2.5 w-2.5" />
                {tier.label}
              </span>
            )}

            {part?.isFounderMember && (
              <span
                className={`absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-white font-semibold border border-yellow-100 bg-yellow-600`}
              >
                Founding Seller
              </span>
            )}
            <span
              className={`absolute bottom-2 left-2 bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded ${
                isCompact ? "text-[10px] px-1 py-0.5" : ""
              }`}
            >
              ${displayPrice}
            </span>
          </div>
        </CardHeader>
        <CardContent className={`${isCompact ? "p-1" : "p-4"} flex-grow`}>
          <div className="flex justify-between items-center">
            <CardTitle
              className={`${
                isCompact ? "text-sm" : "text-lg"
              } font-semibold mb-1 group-hover:text-primary transition-colors line-clamp-2`}
            >
              {partMakeModel} {partYear} {partName}
            </CardTitle>
            {user?.role === "buyer" && isAuthenticated && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveClick(part);
                }}
                disabled={saveLoading || removeLoading}
                className="transition-transform hover:scale-110"
              >
                {isSaved ? (
                  <Bookmark className="w-6 h-6 text-primary fill-primary drop-shadow-md" />
                ) : (
                  <Bookmark className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors duration-300" />
                )}
              </button>
            )}
          </div>
          {/* <p className="text-[11px] sm:text-xs text-muted-foreground truncate">
            {partYear}
          </p>
          <p className="text-[11px] mt-1 sm:text-xs text-muted-foreground truncate">
            {partMakeModel}
          </p> */}
          {!isCompact && (
            <CardDescription className="text-sm py-1 text-muted-foreground line-clamp-2 overflow-hidden text-ellipsis">
              {part.description || `High quality ${partName || "part"}.`}
            </CardDescription>
          )}

          <div
            className={`flex items-center ${
              isCompact ? "text-xs" : "text-sm"
            } text-muted-foreground mt-1`}
          >
            <Building className="w-3 h-3 mr-1.5 text-muted-foreground" />
            {part.sellerName || "Unknown Seller"}
          </div>
        </CardContent>
        {!isCompact &&
        user?.role === "seller" &&
        user?.uid === part?.sellerId ? null : (
          <CardFooter className="p-4 pt-0 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full group"
              onClick={(e) => {
                e.stopPropagation();
                handleChatClick();
              }}
            >
              <MessageSquare className="mr-2 h-4 w-4 group-hover:animate-pulse" />
              Chat
            </Button>
            {part?.stripePaymentLink &&
              part?.stripePaymentLink !== "" &&
              user?.role !== "seller" && (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full group bg-primary text-white hover:bg-primary/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBuyClick();
                  }}
                >
                  <ShoppingCart className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                  Buy Now
                </Button>
              )}
          </CardFooter>
        )}
      </Card>
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
      {showPaymentModal && (
        <PartPaymentModal
          open={showPaymentModal}
          part={part}
          close={() => setShowPaymentModal(false)}
        />
      )}
    </motion.div>
  );
};

export default PartCard;
